/**
 * /admin/production — manufacturing + outsource partner management
 * (iter114s.76).
 *
 * Two slices side-by-side:
 *  1. Active production orders (status ∈ in_production / production / shipped)
 *  2. CustomOrdersTable (admin-keyed manual orders that bypass the
 *     customer wizard but still go through partner dispatch).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from './AdminLayout';
import { OrdersTable } from './OrdersTable';
import { OrderManagementModal } from './OrderManagementModal';
import { CustomOrdersTable } from './CustomOrdersTable';
import { CustomOrderModal } from './CustomOrderModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRODUCTION_STATUSES = new Set(['in_production', 'production', 'shipped']);

const AdminProduction = () => {
  const [orders, setOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [showCustomOrderModal, setShowCustomOrderModal] = useState(false);
  const [editingCustomOrder, setEditingCustomOrder] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/enhanced-orders/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/custom-orders`, { withCredentials: true }),
      ]);
      setOrders((oRes.data || []).filter((o) => PRODUCTION_STATUSES.has((o.status || '').toLowerCase())));
      setCustomOrders(cRes.data?.orders || cRes.data || []);
    } catch { toast.error('Failed to load production queue.'); }
    finally { setLoading(false); }
  }, []);

  const fetchCustomOrders = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/custom-orders`, { withCredentials: true });
      setCustomOrders(r.data?.orders || r.data || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openOrderModal = (o) => {
    setSelectedOrder(o); setTrackingNumber(o.tracking_number || '');
    setOrderStatus(o.status || ''); setProofFile(null); setShowOrderModal(true);
  };
  const closeOrderModal = () => { setShowOrderModal(false); setSelectedOrder(null); };

  const handleUpdate = async () => {
    if (!selectedOrder) return;
    try {
      await axios.put(`${API_URL}/api/enhanced-orders/${selectedOrder.id}/status`, {
        status: orderStatus, tracking_number: trackingNumber || null,
      }, { withCredentials: true });
      toast.success('Order updated.'); fetchAll(); closeOrderModal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed.'); }
  };

  return (
    <AdminLayout>
      <div className="max-w-screen-2xl space-y-8">
        <header>
          <h1 className="text-2xl font-bold text-[#14281D]" data-testid="admin-production-heading">Production</h1>
          <p className="text-sm text-[#1F3D2C]/70">Active manufacturing + outsource partner dispatch + custom-keyed orders.</p>
        </header>
        <section>
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#2F5D3A] mb-2">In production / shipped</h2>
          <OrdersTable orders={orders} loading={loading} onManageOrder={openOrderModal} />
        </section>
        <section>
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#2F5D3A] mb-2">Custom-keyed orders</h2>
          <CustomOrdersTable
            orders={customOrders}
            loading={loading}
            onCreate={() => { setEditingCustomOrder(null); setShowCustomOrderModal(true); }}
            onEdit={(o) => { setEditingCustomOrder(o); setShowCustomOrderModal(true); }}
            onDelete={async (o) => {
              if (!window.confirm(`Delete custom order ${o.order_number || o.id.slice(0, 8)}?`)) return;
              try {
                await axios.delete(`${API_URL}/api/admin/custom-orders/${o.id}`, { withCredentials: true });
                toast.success('Deleted'); fetchCustomOrders();
              } catch { toast.error('Delete failed.'); }
            }}
          />
        </section>
      </div>
      <OrderManagementModal
        open={showOrderModal} order={selectedOrder} proofFile={proofFile}
        trackingNumber={trackingNumber} orderStatus={orderStatus}
        onClose={closeOrderModal} onProofFileChange={setProofFile}
        onUploadProof={() => { /* prod stage doesn't take new proofs */ }}
        onTrackingChange={setTrackingNumber} onStatusChange={setOrderStatus}
        onUpdateStatus={handleUpdate}
      />
      <CustomOrderModal
        open={showCustomOrderModal} existingOrder={editingCustomOrder}
        onClose={() => { setShowCustomOrderModal(false); setEditingCustomOrder(null); }}
        onSaved={() => fetchCustomOrders()}
      />
    </AdminLayout>
  );
};

export default AdminProduction;
